import { type ServerMessage } from '@soulhx/fs-common';
import { db, useAsyncLiveQuery, useBulkUserSettings } from '@soulhx/client-store';
import { getBoolSettingValue } from '@soulhx/user-settings';
import { use } from 'react';
import { CommsContext } from '../CommsContext.js';
import { DateTime } from 'luxon';

interface IUseServerMessagesParams {
  includeRead?: boolean;
}
export const useServerMessages = ({ includeRead = false }: IUseServerMessagesParams = {}): {
  messages: ServerMessage[];
  unfilteredMessages: ServerMessage[];
} => {
  const commsContext = use(CommsContext);
  const { data, isLoading: dataLoading } = useAsyncLiveQuery<ServerMessage[]>(
    async () => {
      return db.serverMessages
        .filter((i) => {
          if (commsContext && commsContext.isAdmin) return true;
          if (i.expiryDate === undefined) return true;

          const todaysDate = DateTime.now();
          const smDate = DateTime.fromISO(i.expiryDate);
          return smDate >= todaysDate;
        })
        .toArray();
    },
    [commsContext],
    []
  );

  const { settings, settingsLoading } = useBulkUserSettings();

  if (data.length < 1 || settingsLoading || dataLoading) return { messages: [], unfilteredMessages: [] };

  let filteredData = data;

  const searchText = commsContext?.searchString;
  if (searchText && searchText.length > 0) {
    filteredData = data.filter((item) => {
      for (let i = 0; i < item.text.length; i++) {
        if (item.text[i].msg.includes(searchText)) {
          return true;
        }
      }

      return false;
    });

    if (filteredData.length < 1) {
      return { messages: [], unfilteredMessages: data };
    }
  }

  if (includeRead) {
    return { messages: filteredData, unfilteredMessages: data };
  }

  filteredData = filteredData.filter((sm) => {
    const settingID = `helpText.serverMessages.${sm.id}`;
    return getBoolSettingValue(settingID, settings);
  });

  return { messages: filteredData, unfilteredMessages: data };
};
